import React, { useState } from 'react';
import { submitContact } from '../services/api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LoadingSpinner from '../components/LoadingSpinner';
import { COMPANY_INFO } from '../config/config';
import '../styles/pages.css';

function Contact() {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', subject: '', message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validateField = (name, value) => {
    switch (name) {
      case 'email':
        if (value.length > 100) return 'Email too long (max 100 characters)';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value) ? '' : 'Invalid email format';
      case 'phone':
        if (value.length > 20) return 'Phone number too long (max 20 characters)';
        const phoneRegex = /^[0-9]{10,15}$/;
        return phoneRegex.test(value.replace(/[\s-]/g, '')) ? '' : 'Invalid phone number';
      case 'name':
        if (value.length > 100) return 'Name too long (max 100 characters)';
        return value.trim() ? '' : 'This field is required';
      case 'subject':
        if (value.length > 200) return 'Subject too long (max 200 characters)';
        return value.trim() ? '' : 'This field is required';
      case 'message':
        if (value.length > 2000) return 'Message too long (max 2000 characters)';
        return value.trim() ? '' : 'This field is required';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({...formData, [name]: value});
    const error = validateField(name, value);
    setErrors({...errors, [name]: error});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fix all errors before submitting');
      return;
    }

    setIsSubmitting(true);
    
    try {
      await submitContact({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        subject: formData.subject.trim(),
        message: formData.message.trim()
      });
      
      toast.success('✅ Message sent successfully! We will contact you soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setErrors({});
    } catch (error) {
      toast.error(error.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <ToastContainer position="top-right" autoClose={5000} />
      {isSubmitting && <LoadingSpinner message="Sending message..." />}
      <section className="hero">
        <div className="container">
          <h1>Contact Us</h1>
          <p>Get in touch with our team</p>
        </div>
      </section>
      <section className="section">
        <div className="container">
          <div className="contact-grid">
            <div>
              <h2 className="section-title">Send us a Message</h2>
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <input 
                    type="text" 
                    name="name"
                    placeholder="Full Name *" 
                    value={formData.name}
                    onChange={handleChange}
                    className={errors.name ? 'error' : ''}
                    maxLength="100"
                    required 
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>
                <div className="form-group">
                  <input 
                    type="email" 
                    name="email"
                    placeholder="Email *" 
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? 'error' : ''}
                    maxLength="100"
                    required 
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>
                <div className="form-group">
                  <input 
                    type="tel" 
                    name="phone"
                    placeholder="Phone *" 
                    value={formData.phone}
                    onChange={handleChange}
                    className={errors.phone ? 'error' : ''}
                    maxLength="20"
                    required 
                  />
                  {errors.phone && <span className="error-message">{errors.phone}</span>}
                </div>
                <div className="form-group">
                  <input 
                    type="text" 
                    name="subject"
                    placeholder="Subject *" 
                    value={formData.subject}
                    onChange={handleChange}
                    className={errors.subject ? 'error' : ''}
                    maxLength="200"
                    required 
                  />
                  {errors.subject && <span className="error-message">{errors.subject}</span>}
                </div>
                <div className="form-group">
                  <textarea 
                    rows="5" 
                    name="message"
                    placeholder="Your Message *" 
                    value={formData.message}
                    onChange={handleChange}
                    className={errors.message ? 'error' : ''}
                    maxLength="2000"
                    required
                  ></textarea>
                  {errors.message && <span className="error-message">{errors.message}</span>}
                </div>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
            <div className="contact-info">
              <h2 className="section-title">Contact Information</h2>
              <p>📍 <strong>Address:</strong><br/>{COMPANY_INFO.address}</p>
              <p>📞 <strong>Phone:</strong><br/>{COMPANY_INFO.phone}</p>
              <p>✉ <strong>Email:</strong><br/>{COMPANY_INFO.email}</p>
              <p>🕒 <strong>Business Hours:</strong><br/>{COMPANY_INFO.businessHours.weekdays}<br/>{COMPANY_INFO.businessHours.saturday}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;
